import React, { useEffect, useState } from "react";
import { Alert, Box, Stack } from "@mui/material";
import {
  DynamicModal,
  DynamicModalProps,
} from "src/components/base/components/DynamicModal";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { SSText } from "src/components/base/components/SSText";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { TCartReservation, TEvent } from "src/shared/types/globalStotre.types";
import { createState } from "src/utils/create_zustand_store";
import { EventCard } from "src/components/feature/homepageFlow/EventCard";
import { formatSqlDate, throttleTime } from "src/utils/utils";
import { AttendanceSeatSelector } from "./AttendanceSeatSelector";
import { useEventAttendance } from "./useEventAttendance";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import {
  getSecondaryConfirmationStatus,
  hasSecondaryConfirmation,
} from "./utils.event-attendance";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";

export const useAttendanceModal = createState({
  isOpen: false,
  event: {} as TEvent,
  seatCount: 1,
});

export function EventAttendanceModal() {
  const theme = useSSTheme();
  const { sm: smUp } = useCssMediaQuery("up");
  const teamConfig = useTeamConfig();
  const { HOME_PAGE_SETTINGS } = teamConfig;
  const modalState = useAttendanceModal();
  const { isReservationEnabled, updateEventAttendance } = useEventAttendance({ event: modalState?.event }); // prettier-ignore
  const [isAttending, setIsAttending] = useState(false);
  const [isNotAttending, setIsNotAttending] = useState(false);
  const { cart, setGlobalState } = useGlobalStore();
  const router = useRouterExt();

  // Reset loading flags whenever the modal opens. The paid-reservation
  // branch sets `isAttending=true` and then redirects to checkout without
  // unmounting this component, so without this reset the next open would
  // inherit the stale `true` and disable every CTA + close icon.
  useEffect(() => {
    if (modalState?.isOpen) {
      setIsAttending(false);
      setIsNotAttending(false);
    }
  }, [modalState?.isOpen]);

  const event = modalState?.event;
  const hasGuestSeatsOnAccount = (event?.seatInformation?.GUEST || 0) > 0;
  const hasAddOnSeatsClaimed = event?.addOns?.some(
    (addOn) => addOn.seatsClaimed > 0
  );
  const isAddOnSeatsClaimed = hasGuestSeatsOnAccount || hasAddOnSeatsClaimed;
  const isOptOutDisabled = Boolean(event?.optedIn) && !Boolean(event?.isOptOutEnabled); // prettier-ignore
  // Paid-reservation events route through cart/checkout and cannot be
  // self-cancelled — only ops can release the assignment. Hide every
  // cancel/opt-out CTA in this modal when the flag is set.
  const isPaidReservation = Boolean(event?.isPaymentRequired);

  // When editing an existing reservation, only enable "Change Seat Count" when
  // the selected count actually differs from what's already reserved — an
  // unchanged submission is a no-op (the BE computes seatCountDifference = 0).
  const isSeatCountUnchanged =
    Boolean(event?.optedIn) &&
    Number(modalState?.seatCount ?? 0) === Number(event?.optedInSeatCount ?? 0);

  // Determine if this event is in secondary confirmation mode
  const isSecondaryFlow = hasSecondaryConfirmation(event);
  const secondaryStatus = isSecondaryFlow
    ? getSecondaryConfirmationStatus(event)
    : "not_applicable";
  const isSecondaryWindowOpen = secondaryStatus === "window_open";
  const isSecondaryConfirmed = secondaryStatus === "confirmed";

  // Primary labels
  const attendEventModalTitle = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.TITLE || "Manage Your Attendance"; // prettier-ignore
  // Paid reservations get a dedicated description (falls back to the default
  // when not configured) so the user knows clicking Reserve routes them to
  // checkout instead of confirming attendance directly.
  const attendEventModalDescription = isPaidReservation
    ? HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.PAID_RESERVATION_DESCRIPTION ||
      HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.DESCRIPTION ||
      null
    : HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.DESCRIPTION || null;
  const changeSeatCountButtonLabel = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.CHANGE_SEAT_COUNT_BUTTON_LABEL || "Change Seat Count"; // prettier-ignore
  const attendButtonLabel = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.ATTEND_BUTTON_LABEL || "Yes! I Will Attend"; // prettier-ignore
  const notAttendButtonLabel = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.NOT_ATTENDING_BUTTON_LABEL || "No, I Can't Make It"; // prettier-ignore

  // Secondary labels
  const secondaryModalTitle = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.SECONDARY_CONFIRMATION_TITLE || "Confirm Your Attendance"; // prettier-ignore
  const secondaryDescription = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.SECONDARY_CONFIRMATION_DESCRIPTION || "You previously opted in to attend this event. Please confirm your attendance before the deadline."; // prettier-ignore
  const secondaryConfirmLabel = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.SECONDARY_CONFIRMATION_BUTTON_LABEL || "Confirm!"; // prettier-ignore
  const secondaryCancelLabel = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.SECONDARY_CANCEL_BUTTON_LABEL || "Cancel Attendance"; // prettier-ignore

  const handleCloseModal = () => {
    modalState.set({ isOpen: false });
    throttleTime(400, () => modalState.reset());
  };

  const handleAttendance = async (optInResponse: boolean) => {
    // Paid-reservation branch: opt-in for events flagged `isPaymentRequired`
    // skips the zero-dollar SSMOP attendance API and routes the user through
    // cart/checkout instead. The four reservation* fields are the per-event
    // hold-target overrides the BE will use against TM during manageCart.
    // Opt-out still goes through the normal API — the BE blocks it when the
    // user has already paid (see checkUserPaidReservationForEvent).
    if (optInResponse && modalState?.event?.isPaymentRequired) {
      setIsAttending(true);
      const event = modalState.event;

      // Change Seats flow: when the user already has a paid reservation for
      // this event, only hold/charge the ADDED seats (new total - existing).
      // Reducing seats is blocked upstream in AttendanceSeatSelector, so the
      // delta is always >= 1 here. existingSeatCount lets the BE record the
      // true total while charging only the difference — no refund scenarios.
      const existingSeatCount = event.optedIn
        ? Number(event.optedInSeatCount ?? 0)
        : 0;
      const requestedSeatCount = modalState?.seatCount ?? 1;
      const seatCountToCharge = requestedSeatCount - existingSeatCount;

      const reservation: TCartReservation = {
        eventCode: event.eventCode,
        reservationEventCode: event.reservationEventCode ?? null,
        reservationTicketTypeCode: event.reservationTicketTypeCode ?? "",
        reservationPriceCode: event.reservationPriceCode ?? null,
        reservationClassName: event.reservationClassName ?? null,
        seatCount: seatCountToCharge,
        existingSeatCount,
        // Seed from the per-event pricing. `price` is optimistic — manageCart
        // overwrites it with TM's real per-seat charge — but serviceFee /
        // additionalFee are kept as-is by manageCart, so these become the
        // fees actually charged. `?? 0` covers unpriced (null) events.
        price: event.price ?? 0,
        serviceFee: event.serviceFee ?? 0,
        additionalFee: event.additionalFee ?? 0,
      };
      const existing = (cart?.reservations || []).filter(
        (r) => r.eventCode !== event.eventCode
      );
      setGlobalState({
        cart: { ...cart, reservations: [...existing, reservation] },
      });
      handleCloseModal();
      router.pushSchema({
        pathname: routes.checkout,
        query: { tab: constants.checkoutTabs.checkout },
      });
      return;
    }

    optInResponse ? setIsAttending(true) : setIsNotAttending(true);
    await updateEventAttendance({
      event: modalState?.event,
      optInResponse,
      seatCount: modalState?.seatCount,
    });
    setIsAttending(false);
    setIsNotAttending(false);
    handleCloseModal();
  };

  const handleSecondaryConfirmation = async (optInResponse: boolean) => {
    optInResponse ? setIsAttending(true) : setIsNotAttending(true);
    await updateEventAttendance({
      event: modalState?.event,
      optInResponse,
      seatCount: modalState?.seatCount,
      isSecondaryConfirmation: true,
    });
    setIsAttending(false);
    setIsNotAttending(false);
    handleCloseModal();
  };

  const actionButtonCommonStyle = {
    width: smUp ? "fit-content" : "100%",
    minWidth: smUp ? "200px" : "100%",
    fontWeight: 600,
  };

  const createActionButtons = (): DynamicModalProps["slot"] => {
    // Secondary confirmation: window open → Confirm / Cancel
    if (isSecondaryWindowOpen) {
      return {
        closeIcon: { isDisabled: isAttending || isNotAttending },
        secondaryButton: {
          isHidden:
            isAddOnSeatsClaimed || isOptOutDisabled || isPaidReservation,
          size: "large",
          label: secondaryCancelLabel,
          variant: "outlined",
          isDisabled: isAttending || isNotAttending,
          isLoading: isNotAttending,
          style: actionButtonCommonStyle,
          onClick: () => handleSecondaryConfirmation(false),
        },
        primaryButton: {
          isHidden: false,
          size: "large",
          label: secondaryConfirmLabel,
          variant: "contained",
          isDisabled: isAttending || isNotAttending,
          isLoading: isAttending,
          style: actionButtonCommonStyle,
          onClick: () => handleSecondaryConfirmation(true),
        },
      };
    }

    // Secondary confirmed → allow opt-out using the primary attendance API
    if (isSecondaryConfirmed) {
      if (isOptOutDisabled) {
        return {
          primaryButton: { label: "Close", onClick: handleCloseModal },
          secondaryButton: { isHidden: true },
        };
      }

      return {
        closeIcon: { isDisabled: isAttending || isNotAttending },
        secondaryButton: {
          isHidden: isAddOnSeatsClaimed || isPaidReservation,
          size: "large",
          label: notAttendButtonLabel,
          variant: "outlined",
          isDisabled: isAttending || isNotAttending,
          isLoading: isNotAttending,
          style: actionButtonCommonStyle,
          onClick: () => handleAttendance(false),
        },
        primaryButton: { label: "Close", onClick: handleCloseModal },
      };
    }

    // --- Existing primary flow buttons (unchanged) ---
    if (isReservationEnabled) {
      if (isOptOutDisabled) {
        return {
          primaryButton: { label: "Close", onClick: handleCloseModal },
          secondaryButton: { isHidden: true },
        };
      }

      const hasSingleSeat = (event?.seatInformation?.PASS || 0) <= 1;

      // Already opted in with single seat — no seat change needed, show cancel + close
      if (modalState?.event?.optedIn && hasSingleSeat) {
        return {
          closeIcon: { isDisabled: isAttending || isNotAttending },
          secondaryButton: {
            isHidden: isAddOnSeatsClaimed || isPaidReservation,
            size: "large",
            label: notAttendButtonLabel,
            variant: "outlined",
            isDisabled: isAttending || isNotAttending,
            isLoading: isNotAttending,
            style: actionButtonCommonStyle,
            onClick: () => handleAttendance(false),
          },
          primaryButton: { label: "Close", onClick: handleCloseModal },
        };
      }

      return {
        closeIcon: { isDisabled: isAttending || isNotAttending },
        secondaryButton: {
          isHidden:
            !Boolean(modalState?.event?.optedIn) ||
            isAddOnSeatsClaimed ||
            isPaidReservation,
          size: "large",
          label: notAttendButtonLabel,
          variant: "outlined",
          isDisabled: isAttending || isNotAttending,
          isLoading: isNotAttending,
          style: actionButtonCommonStyle,
          onClick: () => handleAttendance(false),
        },
        primaryButton: {
          isHidden: false,
          size: "large",
          label: modalState?.event?.optedIn
            ? changeSeatCountButtonLabel
            : attendButtonLabel,
          variant: "contained",
          isDisabled: isAttending || isNotAttending || isSeatCountUnchanged,
          isLoading: isAttending,
          style: actionButtonCommonStyle,
          onClick: () => handleAttendance(true),
        },
      };
    }

    return {
      closeIcon: { isDisabled: isAttending || isNotAttending },
      secondaryButton: {
        isHidden: modalState.event?.optedIn === false || isPaidReservation,
        size: "large",
        label: notAttendButtonLabel,
        variant: "outlined",
        isDisabled: isAttending || isNotAttending,
        isLoading: isNotAttending,
        style: actionButtonCommonStyle,
        onClick: () => handleAttendance(false),
      },
      primaryButton: {
        isHidden: modalState.event?.optedIn === true,
        size: "large",
        label: attendButtonLabel,
        variant: "contained",
        isDisabled: isAttending || isNotAttending,
        isLoading: isAttending,
        style: actionButtonCommonStyle,
        onClick: () => handleAttendance(true),
      },
    };
  };

  // Determine modal title based on flow
  const modalTitle =
    isSecondaryWindowOpen || isSecondaryConfirmed
      ? secondaryModalTitle
      : attendEventModalTitle;

  return (
    <DynamicModal
      isOpen={modalState.isOpen}
      title={modalTitle}
      hideTitleIcon={true}
      onClose={handleCloseModal}
      slot={{ ...createActionButtons() }}
      description={
        <Stack sx={{ userSelect: "none" }}>
          <Box
            sx={{
              opacity:
                isOptOutDisabled &&
                !isSecondaryWindowOpen &&
                !isSecondaryConfirmed
                  ? 0.5
                  : 1,
            }}
          >
            {/* Secondary confirmation alert */}
            {isSecondaryWindowOpen ? (
              <Alert
                severity="warning"
                sx={{ fontSize: "0.9rem", fontWeight: 500, mb: 2 }}
              >
                {secondaryDescription}
                {event?.secondaryEndDateTime ? (
                  <>
                    {" "}
                    Deadline:{" "}
                    <strong>
                      {formatSqlDate(
                        event.secondaryEndDateTime,
                        "EEEE, MMM dd, h:mm a"
                      )}
                    </strong>
                  </>
                ) : null}
              </Alert>
            ) : null}

            {/* Secondary confirmed info */}
            {isSecondaryConfirmed ? (
              <Alert
                severity="success"
                sx={{ fontSize: "0.9rem", fontWeight: 500, mb: 2 }}
              >
                Your attendance for this event has been confirmed.
              </Alert>
            ) : null}

            {/* Primary flow description */}
            {!isSecondaryWindowOpen &&
            !isSecondaryConfirmed &&
            attendEventModalDescription ? (
              <SSText variant="body1" color="text.secondary" mb={2}>
                {attendEventModalDescription}
              </SSText>
            ) : null}

            <EventCard event={modalState.event} showPriceBreakdown />

            {isReservationEnabled && (event?.seatInformation?.PASS || 0) > 1 ? (
              <AttendanceSeatSelector
                isDisabled={
                  isAttending ||
                  isNotAttending ||
                  isOptOutDisabled ||
                  isSecondaryConfirmed
                }
              />
            ) : null}

            {isReservationEnabled && isAddOnSeatsClaimed ? (
              <Alert
                severity="info"
                icon={false}
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                {HOME_PAGE_SETTINGS?.EVENT_CARD
                  ?.RESERVATION_ADD_ON_SEATS_CLAIMED_MESSAGE ||
                  "This reservation cannot be canceled because additional guest tickets have been purchased for the event."}
              </Alert>
            ) : null}
          </Box>

          {isReservationEnabled &&
          isOptOutDisabled &&
          !isSecondaryWindowOpen &&
          !isSecondaryConfirmed ? (
            <Alert
              severity="info"
              icon={false}
              sx={{ fontSize: "0.9rem", fontWeight: 500 }}
            >
              {HOME_PAGE_SETTINGS?.EVENT_CARD
                ?.RESERVATION_OPT_OUT_DISABLED_MESSAGE ||
                "Looks like you're already signed up for this event! You can update your seats or opt out once the option becomes available."}
            </Alert>
          ) : null}
        </Stack>
      }
    />
  );
}
